import * as actions from './actions';

import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccessStoryService } from '@app/services/success-story.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounce,
  debounceTime,
  map,
  mergeMap,
} from 'rxjs/operators';
import { SuccessStory } from './state';

@Injectable()
export class SuccessStoriesEffects {
  constructor(
    private actions$: Actions,
    private successStoryApiService: SuccessStoryService,
    private snakBar: MatSnackBar
  ) {}

  getSuccessStories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getSuccessStories),
      concatMap(action =>
        this.successStoryApiService
          .getAll(action.page ?? 1, action.pageSize ?? 9)
          .pipe(
            map(res => actions.getSuccessStoriesSuccess({ data: res })),
            catchError(e => {
              this.snakBar.open(
                'An error occurred, while loading stories.',
                'Dismiss',
                {
                  duration: 5000,
                  verticalPosition: 'top',
                  horizontalPosition: 'right',
                }
              );
              return of(
                actions.getSuccessStoriesFailure({
                  error: e.error,
                })
              );
            })
          )
      ),
      debounceTime(200)
    );
  });

  addSuccessStories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.addSuccessStories),
      mergeMap(action =>
        this.successStoryApiService
          .create(action.successStory, action.files)
          .pipe(
            map(res =>
              actions.addSuccessStoriesSuccess({
                successStory: res,
              })
            ),
            catchError(e => {
              this.snakBar.open(
                'An error occurred, while loading charts.',
                'Dismiss',
                {
                  duration: 5000,
                  verticalPosition: 'top',
                  horizontalPosition: 'right',
                }
              );
              return of(
                actions.addSuccessStoriesError({
                  error: e.error,
                })
              );
            })
          )
      )
    );
  });

  changeSuccessStoriesSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        actions.addSuccessStoriesSuccess,
        actions.updateSuccessStoriesSuccess
      ),
      map(() => actions.getSuccessStories({ page: 1, pageSize: 18 }))
    );
  });

  updateSuccessStories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.updateSuccessStories),
      mergeMap(action =>
        this.successStoryApiService
          .updateStory(action.successStory, action.files, action.id)
          .pipe(
            map(res => {
              return actions.updateSuccessStoriesSuccess({
                successStory: res as SuccessStory,
              });
            }),
            catchError(e => {
              this.snakBar.open(
                'An error occurred, while loading charts.',
                'Dismiss',
                {
                  duration: 5000,
                  verticalPosition: 'top',
                  horizontalPosition: 'right',
                }
              );
              return of(
                actions.updateSuccessStoriesError({
                  error: e.error,
                })
              );
            })
          )
      )
    );
  });

  getSuccessStoryById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getSuccessStoryById),
      mergeMap(action =>
        this.successStoryApiService.getbyid(action.id).pipe(
          map(res =>
            actions.getSuccessStoryByIdSuccess({
              story: res,
            })
          ),
          catchError(e => {
            this.snakBar.open(
              'An error occurred, while loading success story',
              'Dismiss',
              {
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'right',
              }
            );
            return of(
              actions.updateSuccessStoriesError({
                error: e.error,
              })
            );
          })
        )
      )
    );
  });

  deleteSuccessStoryById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.deleteSuccessStory),
      mergeMap(action =>
        this.successStoryApiService.delete(action.id).pipe(
          map(res =>
            actions.deleteSuccessStorySuccess({
              story: res,
            })
          ),
          catchError(e => {
            this.snakBar.open(
              'An error occurred, while deleting success story',
              'Dismiss',
              {
                duration: 5000,
                verticalPosition: 'top',
                horizontalPosition: 'right',
              }
            );
            return of(
              actions.deleteSuccessStoryError({
                error: e.error,
              })
            );
          })
        )
      )
    );
  });
}
